import { Tab } from '@headlessui/react';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Button, ButtonProps } from '../button';
import { Popover, Trigger } from '../popover';
import { Text } from '@restworld/ui-ds-base';
import { useResponsiveBreakPoints } from '@restworld/utils-common';
import { fontColorMap } from '../button/button.constant';
import { FontWeightType } from '../title';
import { CvParsingLoadingState } from './cv-parsing-loading-state';
import { cn } from '@restworld/utils-common';

export type TabProp = {
  label: string | React.ReactNode;
  content: string | React.ReactNode;
  isActive?: boolean;
  hoverMessage?: string | React.ReactNode;
  icon?: React.ReactNode;
  buttonIcon?: ButtonProps['icon'];
  buttonActiveIcon?: ButtonProps['icon'];
  buttonType?: ButtonProps['variant'];
  activeButtonType?: ButtonProps['variant'];
  badgeLabel?: string | number | React.ReactNode;
  tabIndex?: string | number;
  fontColor?: keyof typeof fontColorMap;
  activeFontColor?: keyof typeof fontColorMap;
  twStyle?: string;
  fontWeight?: FontWeightType;
  activestring?: string;
  isLoading?: boolean;
};

export type TabProps = {
  tabs: TabProp[];
  tabPanelStyling?: string;
  buttonStyling?: string;
  tabListStyling?: string;
  isSticky?: boolean;
  onTabChange?: (index: number) => void;
  defaultTab?: number;
  hasBorderBottom?: boolean;
  mobileStickyTopPx?: number;
  desktopStickyTopPx?: number;
  centerTabs?: boolean;
};

export const CustomTabs = ({
  tabs,
  tabPanelStyling,
  buttonStyling,
  tabListStyling,
  isSticky = false,
  onTabChange,
  defaultTab,
  hasBorderBottom,
  mobileStickyTopPx = 74,
  desktopStickyTopPx = 88,
  centerTabs = false,
}: TabProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabRef = useRef<any>(null);
  const { isDesktop } = useResponsiveBreakPoints();
  const navbarHeight = isDesktop ? desktopStickyTopPx : mobileStickyTopPx;

  useEffect(() => {
    if (defaultTab !== undefined && tabRef?.current) {
      const tabAtIndex = tabRef.current.children[defaultTab];
      if (tabAtIndex) {
        tabAtIndex.click();
        tabRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [defaultTab]);

  useEffect(() => {
    if (defaultTab !== undefined) {
      const tabs = tabRef.current?.children;
      if (tabs && tabs[defaultTab]) {
        tabs[defaultTab].click();
      }
    }
  }, [defaultTab]);

  useEffect(() => {
    if (tabRef.current) {
      const activeTabElement = tabRef.current.querySelector('[data-headlessui-state="selected"]');
      if (activeTabElement) {
        activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTab]);

  const onHandleTabChange = useCallback(
    (index: number) => {
      setActiveTab(index);
      if (onTabChange) {
        onTabChange(index);
      }
    },
    [onTabChange]
  );

  return (
    <Tab.Group defaultIndex={activeTab} onChange={(index) => onHandleTabChange(index)}>
      <StyledTabList
        $tabListStyling={tabListStyling}
        ref={tabRef}
        $isDesktop={isDesktop}
        $hasBorderBottom={hasBorderBottom}
        $mobileStickyTopPx={mobileStickyTopPx}
        $desktopStickyTopPx={desktopStickyTopPx}
        $isSticky={isSticky}
        $centerTabs={centerTabs}
      >
        {tabs.map(
          (
            {
              label,
              isActive = true,
              hoverMessage,
              icon,
              buttonIcon,
              buttonActiveIcon,
              buttonType,
              activeButtonType,
              badgeLabel,
              fontColor = 'black',
              activeFontColor = 'white',
              twStyle,
              activestring,
              fontWeight,
            },
            index
          ) => (
            <Tab key={index} as={Fragment}>
              {({ selected }) => {
                if (isActive) {
                  return (
                    <Button
                      key={index}
                      disabled={!isActive}
                      variant={selected ? activeButtonType || 'primary' : buttonType || 'gray-50'}
                      icon={selected ? buttonActiveIcon : buttonIcon}
                      fontColor={selected ? activeFontColor : fontColor}
                      fitContent={true}
                      twStyle={buttonStyling || (selected ? activestring : twStyle)}
                      fontWeight={fontWeight}
                    >
                      <div className="flex flex-row gap-2 items-center">
                        {icon && (
                          <IconContainer selected={selected} iconColor={selected ? activeFontColor : fontColor}>
                            {icon}
                          </IconContainer>
                        )}
                        {label}
                        {badgeLabel && (
                          <BadgeContainer>
                            <span>{badgeLabel}</span>
                          </BadgeContainer>
                        )}
                      </div>
                    </Button>
                  );
                } else {
                  return (
                    <Popover key={index} trigger={Trigger.HOVER} buttonstring={'font-medium px-4 py-3 rounded-lg'}>
                      <Popover.Button>
                        <span>{label}</span>
                      </Popover.Button>
                      <Popover.Panel>
                        <Text size={3} color="muted" className="text-black whitespace-nowrap">
                          {hoverMessage}
                        </Text>
                      </Popover.Panel>
                    </Popover>
                  );
                }
              }}
            </Tab>
          )
        )}
      </StyledTabList>

      <Tab.Panels className="w-full">
        {tabs.map(({ content, isLoading }, index) => (
          <StyledTabPanel key={index} $tabPanelStyling={tabPanelStyling}>
            {isLoading ? <CvParsingLoadingState /> : content}
          </StyledTabPanel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
};

const IconContainer = ({ iconColor, className, ...props }: any) => (
  <div className={cn(fontColorMap[iconColor], className)} {...props} />
);

const StyledTabPanel = ({ $tabPanelStyling: twStyle, className, ...props }: any) => (
  <Tab.Panel className={cn('mt-6 p-0', twStyle, className)} {...props} />
);

const StyledTabList = ({
  $tabListStyling: twStyle,
  $isSticky,
  $isDesktop,
  $hasBorderBottom,
  $mobileStickyTopPx,
  $desktopStickyTopPx,
  $centerTabs,
  className,
  style,
  ...props
}: any) => (
  <Tab.List
    className={cn(
      'flex space-x-4 mb-4 px-1 pt-6 pb-1 max-w-full overflow-x-auto scrollbar-none',
      $centerTabs && 'justify-center',
      twStyle,
      $isSticky && 'sticky z-30 left-0 right-0 pt-4 pb-6 bg-neutral-10',
      $hasBorderBottom && 'border-b border-gray-300',
      className
    )}
    style={{
      ...style,
      top: $isSticky ? `${$isDesktop ? $desktopStickyTopPx : $mobileStickyTopPx}px` : undefined,
    }}
    {...props}
  />
);

export const BadgeContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center w-6 h-6 min-w-[24px] min-h-[24px] rounded-full text-xs font-semibold text-primary-700 bg-secondary',
      className
    )}
    {...props}
  />
);
